/**
 * Pocket Radar source descriptor — feeds into ingestParsedSource (src/db/ingest.js).
 *
 * Per-source quirks live here; orchestration lives in ingest.js. Replaces
 * the old src/features/pocket-radar/upload.js.
 *
 * PR-specific accommodations:
 *   - Player Name field arrives as one string ("First Last"); descriptor
 *     splits it via splitPlayerName before exposing it as athleteName.
 *   - throw_context comes from the CSV's Activity column (mapped in
 *     column-map.js), not asked of the coach.
 *   - effort_target_pct is a sourceParam (per-upload coach input).
 */

import {
  parsePocketRadarCsv,
  extractFilenameKey,
  splitPlayerName,
} from './column-map.js';

export const pocketRadarSource = {
  // ----- Parsing -----
  parse(csvText) {
    const result = parsePocketRadarCsv(csvText);
    const split = result.playerName ? splitPlayerName(result.playerName) : null;
    return {
      ...result,
      rows: result.throws,
      // null when the CSV mixes athletes (parser flags this by setting
      // playerName=null when more than one Player Name is seen).
      athleteName: split,
    };
  },

  // Filename-first, content-fallback dedup. PR exports default to a
  // "PR_YYYYMMDD_HHMM_<descriptor>.csv" pattern that becomes the natural
  // unique key. For coach-renamed PR files, fall back to the first throw's
  // timestamp + session title — content-stable across renames of the same
  // session.
  extractDedupKey({ filename, parseResult }) {
    const fromFilename = extractFilenameKey(filename);
    if (fromFilename) return fromFilename;
    const firstThrow = parseResult?.rows?.[0];
    if (firstThrow?.thrown_at) {
      const title = (firstThrow.session_title ?? '').replace(/[^\w-]+/g, '_');
      return `prt:${firstThrow.thrown_at}_${title}`;
    }
    return null;
  },

  // ----- Schema constants -----
  archiveSource: 'pocket_radar_csv',
  uploadTable: 'pocket_radar_uploads',
  dedupColumn: 'filename_key',
  dataTable: 'pocket_radar_throws',

  // ----- Row builders -----
  buildUploadRow({ athlete, dedupKey, parseResult, filename, storagePath, sourceParams }) {
    const lastThrowTime = parseResult.rows[parseResult.rows.length - 1].thrown_at;
    const sessionTitle = parseResult.rows[0].session_title ?? null;
    return {
      filename_key: dedupKey,
      source_filename: filename,
      session_title: sessionTitle,
      athlete_id: athlete.id,
      file_path: storagePath,
      effort_target_pct: sourceParams.effortTargetPct ?? null,
      row_count: parseResult.rows.length,
      uploaded_at: lastThrowTime,
    };
  },

  buildDataRows({ uploadId, athlete, parseResult, sourceParams }) {
    return parseResult.rows.map((t) => ({
      upload_id: uploadId,
      athlete_id: athlete.id,
      pitcher_first: t.pitcher_first,
      pitcher_last: t.pitcher_last,
      throw_num: t.throw_num,
      thrown_at: t.thrown_at,
      velo_mph: t.velo_mph,
      unit: t.unit ?? 'MPH',
      throw_context: t.throw_context,
      activity_str: t.activity_str,
      effort_target_pct: sourceParams.effortTargetPct ?? null,
      session_title: t.session_title,
      video_flag: !!t.video_flag,
      raw: t.raw,
    }));
  },

  // ----- Status payload extras -----
  buildEmptyExtras({ parseResult }) {
    // pitchCount is an alias kept for ta-upload's renderFileRow which
    // says "${r.pitchCount} pitches" for both sources.
    return {
      throwCount: parseResult.rows.length,
      pitchCount: parseResult.rows.length,
    };
  },

  buildSuccessExtras({ parseResult, sourceParams }) {
    return {
      throwCount: parseResult.rows.length,
      pitchCount: parseResult.rows.length,
      filteredCount: parseResult.filteredCount,
      unknownColumns: parseResult.unknownColumns,
      effortTargetPct: sourceParams.effortTargetPct ?? null,
    };
  },
};
